import { SqlOptimizationServer,SqlServerBasicDetails } from '@amzn/awscat-portfolio-assessment-types';
import { Box, TableProps } from '@amzn/awsui-components-react';

import { constants } from '../../../common/Localization';
import { formatCurrencyNoDecimals, formatNumberAsString, formatPercentage } from '../../../common/LocalizationHelper';
import { ColumnDefinition, ColumnTypes } from '../../../common/table/types';
import { roundToInteger } from '../../../common/util';
import { getCell } from '../../common/awsui-table-helper';

type PickedSqlServerProps = 'awsWithConsolidation' | 'countDifferenceWithConsolidation' | 'costDifferenceWithConsolidation' | 'onPremises';
export type OLASummaryItem = Pick<SqlServerBasicDetails, PickedSqlServerProps> & {
    edition: string;
};
export const summaryTableCols = (currency: string):TableProps.ColumnDefinition<OLASummaryItem>[] => [{
    id: 'edition',
    header: constants.OLA_SUMMARY_TABLE_EDITION,
    cell: (item: OLASummaryItem) => (<Box float='left'>{item.edition}</Box>),
}, {
    id: 'onPremCount',
    header: constants.OLA_SUMMARY_TABLE_ONPREM,
    cell: (item: OLASummaryItem) => (<Box float='right'>{item.onPremises}</Box>),
}, {
    id: 'optimizedCount',
    header: constants.OLA_SUMMARY_TABLE_OPTIMIZED,
    cell: (item: OLASummaryItem) => (<Box float='right'>{item.awsWithConsolidation}</Box>),
}, {
    id: 'reduction',
    header: constants.OLA_SUMMARY_TABLE_REDUCTION,
    cell: (item: OLASummaryItem) => (<Box float='right'>{item.countDifferenceWithConsolidation}</Box>),
}, {
    id: 'saving',
    header: constants.OLA_SUMMARY_TABLE_SAVING,
    cell: (item: OLASummaryItem) => (
        <Box float='right'>
            {formatCurrencyNoDecimals(item.costDifferenceWithConsolidation, currency)}
        </Box>),
}];
export const sqlServerTableCols = (currency: string): ColumnDefinition<SqlOptimizationServer>[] => {
    const sqlServerTableColDefs = [{
        id: 'serverId',
        header: constants.SERVER_ID,
        cell: (item: SqlOptimizationServer) => item.serverId,
        type: ColumnTypes.TEXT,
    }, {
        id: 'isPhysical',
        header: constants.OLA_SQL_SERVER_TABLE_IS_PHYSICAL,
        data: (item: SqlOptimizationServer) => item.isPhysical != null ?
            (item.isPhysical ? constants.OLA_SQL_SERVER_TABLE_PHYSICAL : constants.OLA_SQL_SERVER_TABLE_VIRTUAL) : '-',
        cell: (item: SqlOptimizationServer) => item.isPhysical != null ?
            (item.isPhysical ? constants.OLA_SQL_SERVER_TABLE_PHYSICAL : constants.OLA_SQL_SERVER_TABLE_VIRTUAL) : '-',
        type: ColumnTypes.BOOLEAN,
    }, {
        id: 'isReplica',
        header: constants.OLA_SQL_SERVER_TABLE_IS_REPLICA,
        data: (item: SqlOptimizationServer) => item.isReplica != null ? (item.isReplica ? constants.YES : constants.NO) : '-',
        cell: (item: SqlOptimizationServer) => item.isReplica != null ? (item.isReplica ? constants.YES : constants.NO) : '-',
        type: ColumnTypes.BOOLEAN,
    }, {
        id: 'databaseIds',
        header: constants.DATABASE_ID,
        data: (item: SqlOptimizationServer) => item.databaseIds.join(', '),
        cell: (item: SqlOptimizationServer) => item.databaseIds.join(', '),
        type: ColumnTypes.TEXT,
    }, {
        id: 'edition',
        header: constants.OLA_SQL_SERVER_TABLE_EDITION,
        cell: (item: SqlOptimizationServer) => item.edition,
        type: ColumnTypes.TEXT,
    }, {
        id: 'cpus',
        header: constants.OLA_SQL_SERVER_TABLE_ON_PREM_CPU,
        cell: (item: SqlOptimizationServer) => getCell(formatNumberAsString(item.cpus)),
        type: ColumnTypes.NUMBER,
    }, {
        id: 'cores',
        header: constants.OLA_SQL_SERVER_TABLE_ON_PREM_CORES,
        cell: (item: SqlOptimizationServer) => getCell(formatNumberAsString(item.cores)),
        type: ColumnTypes.NUMBER,
    }, {
        id: 'threads',
        header: constants.OLA_SQL_SERVER_TABLE_ON_PREM_THREAD,
        cell: (item: SqlOptimizationServer) => getCell(formatNumberAsString(item.threads)),
        type: ColumnTypes.NUMBER,
    }, {
        id: 'usage',
        header: constants.OLA_SQL_SERVER_TABLE_ON_PREM_USAGE,
        cell: (item: SqlOptimizationServer) => getCell(formatPercentage(item.usage)),
        type: ColumnTypes.NUMBER
    }, {
        id: 'onPremLicenses',
        header: constants.OLA_SQL_SERVER_TABLE_ON_PREM_LICENSE,
        data: (item: SqlOptimizationServer) => item.onPremLicenses ?? '-',
        cell: (item: SqlOptimizationServer) => item.onPremLicenses ?? '-',
        type: ColumnTypes.TEXT,
    }, {
        id: 'ec2InstanceType',
        header: constants.OLA_SQL_SERVER_TABLE_EC2_INSTANCE,
        data: (item: SqlOptimizationServer) => item.ec2Instance.instanceType,
        cell: (item: SqlOptimizationServer) => item.ec2Instance.instanceType,
        type: ColumnTypes.TEXT,
    }, {
        id: 'ec2InstanceVcpu',
        header: constants.OLA_SQL_SERVER_TABLE_EC2_INSTANCE_VCPU,
        data: (item: SqlOptimizationServer) => item.ec2Instance.vcpu,
        cell: (item: SqlOptimizationServer) => item.ec2Instance.vcpu,
        type: ColumnTypes.NUMBER,
    }, {
        id: 'awsLicense',
        header: constants.OLA_SQL_SERVER_TABLE_AWS_LICENSE,
        data: (item: SqlOptimizationServer) => item.awsLicenses,
        cell: (item: SqlOptimizationServer) => getCell(formatNumberAsString(item.awsLicenses)),
        type: ColumnTypes.NUMBER,
    }, {
        id: 'countDifference',
        header: constants.OLA_SQL_SERVER_TABLE_COUNT_DIFF,
        cell: (item: SqlOptimizationServer) => getCell(formatNumberAsString(item.countDifference)),
        type: ColumnTypes.NUMBER,
    }, {
        id: 'costDifference',
        header: constants.OLA_SQL_SERVER_TABLE_COST_DIFF,
        data: (item: SqlOptimizationServer) => roundToInteger(item.costDifference),
        cell: (item: SqlOptimizationServer) => getCell(formatCurrencyNoDecimals(item.costDifference, currency)),
        type: ColumnTypes.NUMBER,
    }, {
        id: 'isConsolidated',
        header: constants.OLA_SQL_SERVER_TABLE_IS_CONSOLIDATED,
        data: (item: SqlOptimizationServer) => item.isConsolidated != null ? (item.isConsolidated ? constants.YES : constants.NO) : '-',
        cell: (item: SqlOptimizationServer) => item.isConsolidated != null ? (item.isConsolidated ? constants.YES : constants.NO) : '-',
        type: ColumnTypes.BOOLEAN,
    
    }, {
        id: 'awsLicensesWithConsolidation',
        header: constants.OLA_SQL_SERVER_TABLE_AWS_LICENSE_CONSOLIDATED,
        cell: (item: SqlOptimizationServer) => getCell(formatNumberAsString(item.awsLicensesWithConsolidation)),
        type: ColumnTypes.NUMBER,
    }, {
        id: 'countDifferenceWithConsolidation',
        header: constants.OLA_SQL_SERVER_TABLE_COUNT_DIFF_CONSOLIDATED,
        cell: (item: SqlOptimizationServer) => getCell(formatNumberAsString(item.countDifferenceWithConsolidation)),
        type: ColumnTypes.NUMBER,
    }, {
        id: 'costDifferenceWithConsolidation',
        header: constants.OLA_SQL_SERVER_TABLE_COST_DIFF_CONSOLIDATED,
        data: (item: SqlOptimizationServer) => roundToInteger(item.costDifferenceWithConsolidation),
        cell: (item: SqlOptimizationServer) => getCell(formatCurrencyNoDecimals(item.costDifferenceWithConsolidation, currency)),
        type: ColumnTypes.NUMBER,
    }];

    const colWithText = sqlServerTableColDefs.map(col => ({
        ...col,
        text: col.header,
    }));

    return colWithText;
}; 

export const sqlServerTableHiddenColumns = ['databaseIds', 'cpus', 'cores', 'threads', 'usage', 'ec2InstanceType', 'ec2InstanceVcpu'];
